import { Suspense, useEffect, useLayoutEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Outlet, useLocation } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { ScrollProgress } from '../motion/interactive';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SectionReveals() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const main = document.getElementById('main');
    if (!main) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('cinematic-section--visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    const prepareSections = () => {
      const sections = Array.from(main.querySelectorAll<HTMLElement>('section'));
      sections.forEach((section, index) => {
        if (!section.classList.contains('cinematic-section')) {
          section.classList.add('cinematic-section');
          if (index === 0) section.classList.add('cinematic-section--visible');
        }
        observer.observe(section);
      });
    };

    prepareSections();
    const mutations = new MutationObserver(prepareSections);
    mutations.observe(main, { childList: true, subtree: true });

    return () => {
      mutations.disconnect();
      observer.disconnect();
    };
  }, [pathname]);

  return null;
}

function RouteTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const reduce = useReducedMotion();

  return (
    <motion.div
      key={pathname}
      className="route-transition"
      initial={reduce ? false : { opacity: 0, y: 34, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={reduce ? { duration: 0 } : { duration: 1.15, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function Layout() {
  return (
    <div className="site-shell">
      <ScrollToTop />
      <ScrollProgress />
      <a href="#main" className="skip-link">Skip to content</a>
      <Navbar />
      <main id="main" tabIndex={-1}>
        <SectionReveals />
        <RouteTransition>
          <Suspense fallback={<div className="route-loading">Loading…</div>}>
            <Outlet />
          </Suspense>
        </RouteTransition>
      </main>
      <Footer />
    </div>
  );
}
